import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { Typography, Link, Tooltip, VaultButton, Avatar } from 'components'
import TableCell from '@material-ui/core/TableCell'
import { colors, CONSTANTS, routes } from 'constants/index'
import { ReactComponent as DescriptionLogo } from 'static/activities/description.svg'
import { ReactComponent as ShareIcon } from 'static/cases/share.svg'
import moment from 'moment-timezone'
import { useDispatch, useSelector } from 'react-redux'
import {
  navigateToCaseDetailsInNewTab,
  navigateToEditCaseInNewTab,
  navigateToProConnectionsInNewTab,
} from 'store/actions/navigation'
import { Link as RouterLink } from 'react-router-dom'
import ProviderPopover from '../common/ProviderPopover'
import PeopleWithAccessModal from '../cases/PeopleWithAccessModal'

const useStyles = makeStyles({
  bulletCell: {
    width: 10,
  },
  circle: {
    height: 10,
    width: 10,
    borderRadius: '50%',
    backgroundColor: colors.BASIC.COLOR[20],
  },
  activeBullet: {
    backgroundColor: colors.SUCCESS,
  },
  link: {
    color: colors.BLUE_20,
    textDecoration: 'none',
  },
  tooltip: {
    padding: 16,
  },
  inactiveCase: {
    color: colors.BASIC.COLOR[20],
  },
  feeType: {
    cursor: 'pointer',
  },
  tableCellTextClass: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  amountCellTextClass: {
    textAlign: 'end',
  },
  cell: {
    maxWidth: 1,
  },
  tooltipText: {
    fontSize: 13,
  },
  btnContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  shareButton: {
    width: 16,
    height: 16,
    marginLeft: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  shareButtonRotated: {
    transform: 'rotate(180deg)',
  },
  shareTooltip: {
    backgroundColor: colors.BASIC.COLOR[80],
    color: colors.WHITE,
    fontSize: 13,
    fontWeight: 'normal',
  },
  providerCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  providerAvatar: {
    width: 24,
    height: 24,
    fontSize: 9.75,
    fontWeight: 400,
  },
})

const ACTIVITIES_TYPE = {
  TIME: 0,
  EXPENSE: 1,
}

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : ''
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return first + last || '?'
}

const ProActivitiesTableRow = ({ data, timezone, isAccountant, onViewSharedClick }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [providerPopoverAnchor, setProviderPopoverAnchor] = useState(null)
  const [showPeopleModal, setShowPeopleModal] = useState(false)
  const currentUser = useSelector(state => state.auth.currentUser)
  const contactInfo = useSelector(state => state.profileCreation.profileData.contactInfo)
  const userCountry = contactInfo?.country || currentUser?.country || 840 // Default to USA

  const isSharedByMe = data.is_shared && data.is_master
  const isSharedWithMe = data.is_shared && !data.is_master

  const handleProviderAvatarClick = e => {
    e.stopPropagation()
    setProviderPopoverAnchor(e.currentTarget)
  }

  const handleProviderPopoverClose = () => {
    setProviderPopoverAnchor(null)
  }

  const handleShareIconClick = e => {
    e.stopPropagation()
    e.preventDefault()
    if (data.case?.uuid) {
      setShowPeopleModal(true)
    }
  }

  const getShareTooltip = () => {
    if (isSharedWithMe) {
      return `Shared with you by ${data.shared_by}`
    } else if (isSharedByMe) {
      return `${isAccountant ? 'Project' : 'Case'} shared by you with ${data.shared_with_count || 0} provider(s)`
    }
    return ''
  }

  const getCurrencySymbol = () => {
    return userCountry === 124 ? 'C$' : '$' // 124 is Canada, 840 is USA
  }
  const timeLabel = data.activityType === ACTIVITIES_TYPE.TIME ? `${data.hours}h ${data.minutes}m` : '-'
  const chargeLabel = !data.billable
    ? 'N/A'
    : data.clientCharge
    ? `${getCurrencySymbol()}${data.clientCharge}`
    : 'no charge'

  const invoiceDate = moment(data.invoiceDate)
    .tz(timezone)
    .format(CONSTANTS.DATE_FORMAT.FORMAT_ONE)

  const invoiceLabel =
    data.billable && data.invoiceDate ? (
      data.invoice ? (
        <Link
          className={classes.link}
          component={RouterLink}
          to={`${routes.getProAccountFinancesInvoicesViewPath()}/${data.invoice}`}
          target="_blank"
        >
          {invoiceDate}
        </Link>
      ) : (
        invoiceDate
      )
    ) : (
      'N/A'
    )

  const navigateToCase = () => {
    dispatch(navigateToCaseDetailsInNewTab(data.case.uuid))
  }

  const navigateToClient = () => {
    dispatch(navigateToProConnectionsInNewTab({ clientName: data.case.client_name }))
  }

  const clientName = data.isClientDeleted ? (
    <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
      {data.case.client_name}
    </Typography>
  ) : (
    <Link onClick={navigateToClient} className={classes.link}>
      {data.case.client_name}
    </Link>
  )

  return (
    <>
      <TableCell>{clientName}</TableCell>
      <TableCell>
        <div className={classes.btnContainer}>
          <Link onClick={navigateToCase} className={classes.link}>
            {data.case.name}
          </Link>
          {data.is_shared && data?.isProjectShared && getShareTooltip() && (
            <Tooltip tooltipClass={classes.shareTooltip} title={getShareTooltip()} placement="bottom-start">
              <ShareIcon
                className={`${classes.shareButton} ${isSharedWithMe ? classes.shareButtonRotated : ''}`}
                onClick={handleShareIconClick}
              />
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell>
        {data.description && (
          <Tooltip
            title={
              <Typography className={classes.tooltipText} variant="subtitle1">
                {data.description}
              </Typography>
            }
            tooltipClass={classes.tooltip}
          >
            <DescriptionLogo />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Tooltip
          title={
            <Typography className={classes.tooltipText} variant="subtitle1">
              {CONSTANTS.ACTIVITY_TYPE(isAccountant)[data.activityType].hint}
            </Typography>
          }
          tooltipClass={classes.tooltip}
        >
          {CONSTANTS.ACTIVITY_TYPE(isAccountant)[data.activityType].icon}
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {timeLabel}
        </Typography>
      </TableCell>
      {isAccountant && (
        <TableCell>
          {data.created_by ? (
            <>
              <div className={classes.providerCell} onClick={handleProviderAvatarClick}>
                <Avatar
                  className={classes.providerAvatar}
                  src={data.created_by.photo_thumbnail || data.created_by.photo || null}
                >
                  {!data.created_by.photo_thumbnail &&
                    !data.created_by.photo &&
                    getInitials(data.created_by.first_name, data.created_by.last_name)}
                </Avatar>
              </div>
              <ProviderPopover
                provider={data.created_by}
                anchorEl={providerPopoverAnchor}
                open={Boolean(providerPopoverAnchor)}
                onClose={handleProviderPopoverClose}
              />
            </>
          ) : (
            <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
              -
            </Typography>
          )}
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {moment(data.creationDate)
            .tz(timezone)
            .format(CONSTANTS.DATE_FORMAT.FORMAT_ONE)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {chargeLabel}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" classes={{ root: classes.tableCellTextClass }}>
          {invoiceLabel}
        </Typography>
      </TableCell>
      <TableCell>
        <VaultButton
          isInitialCase={data.case.fee_type === 0}
          options={
            data?.is_shared && !isSharedByMe
              ? {
                  userID: data.case.client,
                  caseID: data.case.uuid,
                  sharedBy: data.case?.main_pro_uuid,
                }
              : { userID: data.case.client, caseID: data.case.uuid }
          }
          vaultPlace="activities"
          disablePropText={'You will be able to add files once the project is shared with you'}
          disabled={!data.is_vault_or_chat_available}
        />
      </TableCell>
      <PeopleWithAccessModal
        isOpen={showPeopleModal}
        onClose={() => setShowPeopleModal(false)}
        caseUuid={data.case?.uuid}
      />
    </>
  )
}

export default ProActivitiesTableRow
